import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line,
  BarChart, Bar,
  ComposedChart, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import { analyticsAPI } from '../../services/api';
import {
  Euro, Users, TrendingUp, FileText,
  Download, RefreshCw, AlertCircle, BarChart3,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminAnalytics.css';

/* ─────────────── constants ─────────────── */
const DATE_RANGES = (t) => [
  { label: t('admin.analytics.range7'),  days: 7  },
  { label: t('admin.analytics.range30'), days: 30 },
  { label: t('admin.analytics.range90'), days: 90 },
];

const FUNNEL_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#10b981'];

const AXIS_STYLE = { fill: '#71717a', fontSize: 12 };

/* ─────────────── helpers ─────────────── */
const fmt = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n ?? 0);

const fmtShort = (n) =>
  new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(n ?? 0);

const toStartDate = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
};

const downloadCSV = (revenueTrend, header) => {
  const rows = revenueTrend
    .map((r) => `${r.month},${r.revenue},${r.orders}`)
    .join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `meccaflex-analytics-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ─────────────── custom tooltip ─────────────── */
const DarkTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="analytics-tooltip">
      <p className="analytics-tooltip-label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, margin: '2px 0', fontSize: 13 }}>
          {p.name}: {currency && p.dataKey === 'revenue' ? fmt(p.value) : fmtShort(p.value)}
        </p>
      ))}
    </div>
  );
};

/* ─────────────── KPI card ─────────────── */
const KpiCard = ({ icon: Icon, iconBg, label, value, sub, subUp }) => (
  <div className="stat-card">
    <div className="stat-icon" style={{ background: iconBg }}>
      <Icon size={24} color="#fff" />
    </div>
    <div className="stat-content">
      <p className="stat-label">{label}</p>
      <p className="stat-value" style={{ fontSize: '1.6rem' }}>{value}</p>
      {sub !== undefined && (
        <p className="analytics-kpi-sub" style={{ color: subUp ? '#10b981' : '#ef4444' }}>
          {subUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {sub}
        </p>
      )}
    </div>
  </div>
);

/* ─────────────── chart card ─────────────── */
const ChartCard = ({ title, children }) => (
  <div className="analytics-chart-card">
    <h3 className="analytics-chart-title">{title}</h3>
    {children}
  </div>
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
const AdminAnalytics = () => {
  const { t } = useTranslation();

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [rangeDays, setRangeDays] = useState(30);

  /* ── funnel data derived from analytics ── */
  const funnelData = analytics
    ? [
        { stage: t('admin.analytics.funnelNew'),       count: analytics.leadsByStatus?.new       ?? 0 },
        { stage: t('admin.analytics.funnelContacted'), count: analytics.leadsByStatus?.contacted  ?? 0 },
        { stage: t('admin.analytics.funnelQuoted'),    count: analytics.leadsByStatus?.quoted     ?? 0 },
        { stage: t('admin.analytics.funnelConverted'), count: analytics.leadsByStatus?.converted  ?? 0 },
      ]
    : [];

  /* ── revenue trend ── */
  const revenueTrend = analytics?.orders?.revenueTrend ?? [];

  /* ── leads daily trend ── */
  const leadsTrend = (analytics?.leadsTrend ?? []).map((d) => ({
    date: d.date?.slice(5) ?? d.date, // show MM-DD
    count: d.count,
  }));

  /* ── revenue vs last month ── */
  const revenueThisMonth = analytics?.orders?.revenueThisMonth ?? 0;
  const revenueLastMonth = analytics?.orders?.revenueLastMonth ?? 0;
  const revenueDelta =
    revenueLastMonth > 0
      ? (((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100).toFixed(1)
      : null;

  /* ── fetch ── */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startDate = toStartDate(rangeDays);
      const res = await analyticsAPI.getOverview({ startDate });
      setAnalytics(res.data.analytics);
    } catch (err) {
      console.error('AdminAnalytics: fetch error', err);
      setError(err?.response?.data?.message ?? t('admin.analytics.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  useEffect(() => { load(); }, [load]);

  /* ── loading state ── */
  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <div className="admin-loading">
            <RefreshCw size={40} className="spin" color="#f97316" />
            <p>{t('admin.analytics.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  /* ── error state ── */
  if (error) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <div className="admin-empty" style={{ marginTop: '4rem' }}>
            <AlertCircle size={56} color="#ef4444" />
            <h3 style={{ color: '#fff', marginTop: '1rem' }}>{t('admin.analytics.errorTitle')}</h3>
            <p style={{ color: '#a1a1aa', margin: '0.5rem 0 1.5rem' }}>{error}</p>
            <button className="admin-btn admin-btn-primary" onClick={load}>
              <RefreshCw size={16} />
              {t('admin.analytics.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── main render ── */
  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">

        {/* ── header ── */}
        <header className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <BarChart3 size={28} color="#f97316" />
              {t('admin.analytics.title', 'Analytics')}
            </h1>
            <p>{t('admin.analytics.subtitle', 'Panoramica delle performance di business')}</p>
          </div>

          <div className="admin-header-right" style={{ alignItems: 'center' }}>
            {/* date range selector */}
            <div className="analytics-range-group">
              {DATE_RANGES(t).map((r) => (
                <button
                  key={r.days}
                  className={`analytics-range-btn${rangeDays === r.days ? ' active' : ''}`}
                  onClick={() => setRangeDays(r.days)}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <LanguageToggle />

            <button
              className="admin-btn admin-btn-secondary"
              disabled={!analytics || revenueTrend.length === 0}
              onClick={() => downloadCSV(revenueTrend, t('admin.analytics.csvHeader') + '\n')}
              title={t('admin.analytics.downloadCsvTitle')}
            >
              <Download size={16} />
              {t('admin.analytics.exportCsv')}
            </button>

            <button className="admin-btn admin-btn-secondary" onClick={load} title={t('admin.analytics.refresh')}>
              <RefreshCw size={16} />
            </button>
          </div>
        </header>

        {/* ── KPI row ── */}
        <div className="admin-stats">
          <KpiCard
            icon={Euro}
            iconBg="linear-gradient(135deg,#f97316,#ea580c)"
            label={t('admin.analytics.kpiRevenue')}
            value={fmt(revenueThisMonth)}
            sub={revenueDelta !== null ? t('admin.analytics.kpiRevenueSub', { sign: Number(revenueDelta) >= 0 ? '+' : '', delta: Math.abs(revenueDelta) }) : undefined}
            subUp={Number(revenueDelta) >= 0}
          />
          <KpiCard
            icon={Users}
            iconBg="linear-gradient(135deg,#3b82f6,#2563eb)"
            label={t('admin.analytics.kpiLeads')}
            value={fmtShort(analytics?.totalLeads)}
          />
          <KpiCard
            icon={TrendingUp}
            iconBg="linear-gradient(135deg,#10b981,#059669)"
            label={t('admin.analytics.kpiConversionRate')}
            value={`${(analytics?.conversionRate ?? 0).toFixed(1)}%`}
          />
          <KpiCard
            icon={FileText}
            iconBg="linear-gradient(135deg,#8b5cf6,#7c3aed)"
            label={t('admin.analytics.kpiQuoteAcceptance')}
            value={`${(analytics?.quotes?.quoteConversionRate ?? 0).toFixed(1)}%`}
          />
        </div>

        {/* ── secondary stats row ── */}
        <div className="analytics-secondary-row">
          <div className="analytics-secondary-card">
            <span className="analytics-secondary-label">{t('admin.analytics.statsOrdersMonth')}</span>
            <span className="analytics-secondary-value">{analytics?.orders?.ordersThisMonth ?? 0}</span>
          </div>
          <div className="analytics-secondary-card">
            <span className="analytics-secondary-label">{t('admin.analytics.statsInProduction')}</span>
            <span className="analytics-secondary-value" style={{ color: '#f97316' }}>
              {analytics?.orders?.activeProductionOrders ?? 0}
            </span>
          </div>
          <div className="analytics-secondary-card">
            <span className="analytics-secondary-label">{t('admin.analytics.statsOverdueOrders')}</span>
            <span className="analytics-secondary-value" style={{ color: '#ef4444' }}>
              {analytics?.orders?.overdueOrders ?? 0}
            </span>
          </div>
          <div className="analytics-secondary-card">
            <span className="analytics-secondary-label">{t('admin.analytics.statsPendingQuotes')}</span>
            <span className="analytics-secondary-value" style={{ color: '#f59e0b' }}>
              {analytics?.quotes?.pendingQuotes ?? 0}
            </span>
          </div>
          <div className="analytics-secondary-card">
            <span className="analytics-secondary-label">{t('admin.analytics.statsExpiringQuotes')}</span>
            <span className="analytics-secondary-value" style={{ color: '#ef4444' }}>
              {analytics?.quotes?.expiringQuotes ?? 0}
            </span>
          </div>
          <div className="analytics-secondary-card">
            <span className="analytics-secondary-label">{t('admin.analytics.statsConvertedLeads')}</span>
            <span className="analytics-secondary-value" style={{ color: '#10b981' }}>
              {analytics?.convertedLeads ?? 0}
            </span>
          </div>
        </div>

        {/* ── charts 2×2 grid ── */}
        <div className="analytics-charts-grid">

          {/* 1 — Revenue Trend */}
          <ChartCard title={t('admin.analytics.chartRevenueTrend')}>
            {revenueTrend.length === 0 ? (
              <p className="analytics-no-data">{t('admin.analytics.noData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={revenueTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" tick={AXIS_STYLE} />
                  <YAxis tickFormatter={(v) => `€${fmtShort(v)}`} tick={AXIS_STYLE} width={70} />
                  <Tooltip content={<DarkTooltip currency />} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#f97316"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#f97316', strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                    name={t('admin.analytics.seriesRevenue')}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 2 — Lead Conversion Funnel */}
          <ChartCard title={t('admin.analytics.chartLeadFunnel')}>
            {funnelData.every((d) => d.count === 0) ? (
              <p className="analytics-no-data">{t('admin.analytics.noLeads')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={funnelData}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                  <XAxis type="number" tick={AXIS_STYLE} />
                  <YAxis type="category" dataKey="stage" tick={AXIS_STYLE} width={80} />
                  <Tooltip content={<DarkTooltip />} />
                  <Bar dataKey="count" name="Lead" radius={[0, 6, 6, 0]}>
                    {funnelData.map((_, i) => (
                      <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 3 — Daily Leads Trend */}
          <ChartCard title={t('admin.analytics.chartNewLeads')}>
            {leadsTrend.length === 0 ? (
              <p className="analytics-no-data">{t('admin.analytics.noData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={leadsTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tick={AXIS_STYLE} interval="preserveStartEnd" />
                  <YAxis tick={AXIS_STYLE} allowDecimals={false} />
                  <Tooltip content={<DarkTooltip />} />
                  <Bar dataKey="count" name={t('admin.analytics.seriesNewLeads')} fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 4 — Revenue vs Orders composed */}
          <ChartCard title={t('admin.analytics.chartRevenueVsOrders')}>
            {revenueTrend.length === 0 ? (
              <p className="analytics-no-data">{t('admin.analytics.noData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={revenueTrend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" tick={AXIS_STYLE} />
                  <YAxis
                    yAxisId="orders"
                    orientation="left"
                    tick={AXIS_STYLE}
                    allowDecimals={false}
                    label={{ value: t('admin.analytics.axisOrders'), angle: -90, position: 'insideLeft', fill: '#71717a', fontSize: 11 }}
                  />
                  <YAxis
                    yAxisId="revenue"
                    orientation="right"
                    tickFormatter={(v) => `€${fmtShort(v)}`}
                    tick={AXIS_STYLE}
                    width={75}
                  />
                  <Tooltip content={<DarkTooltip currency />} />
                  <Legend
                    wrapperStyle={{ color: '#a1a1aa', fontSize: 13 }}
                  />
                  <Bar
                    yAxisId="orders"
                    dataKey="orders"
                    name={t('admin.analytics.seriesOrders')}
                    fill="rgba(59,130,246,0.55)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId="revenue"
                    type="monotone"
                    dataKey="revenue"
                    name={t('admin.analytics.seriesRevenue')}
                    stroke="#f97316"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#f97316', strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

        </div>{/* end charts grid */}
      </div>{/* end admin-content */}
    </div>
  );
};

export default AdminAnalytics;
